import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import intlShape from '@/shared/utils/intlShape';
import OkCancelModal from '@/shared/components/OkCancelModal';
import CurrencySymbol from '@/shared/components/CurrencySymbol';
import FormattedCurrency from '@/shared/components/FormattedCurrency';
import DelayedDateRangePicker from '@/shared/components/input/DelayedDateRangePicker';
import enumCache from '@/shared/services/EnumCacheService';
import utils from '@/shared/utils/adminpulse';
import messages from '../employeeMessages';

const UpdateEmployeeAmountModal = ({
  show,
  intl,
  onSave,
  onClose,
  title,
  exampleAmount,
  showRecalcRegistrations = false,
}) => {
  const [updateMethods, setUpdateMethods] = useState([]);
  const [roundingMethods, setRoundingMethods] = useState([]);
  const [selectedUpdateMethod, setSelectedUpdateMethod] = useState(0);
  const [amountOrPercentage, setAmountOrPercentage] = useState(0);
  const [selectedRoundingMethod, setSelectedRoundingMethod] = useState(0);
  const [roundingDecimals, setRoundingDecimals] = useState(2);
  const [touched, setTouched] = useState(false);
  const [updateRegistrations, setUpdateRegistrations] = useState(false);
  const [registrationStartDate, setRegistrationStartDate] = useState(
    moment()
      .utc()
      .startOf('day'),
  );
  const [registrationEndDate, setRegistrationEndDate] = useState(
    moment()
      .utc()
      .startOf('day'),
  );

  useEffect(() => {
    Promise.all([
      enumCache.getTranslated('updateAmountMethod', intl),
      enumCache.getTranslated('roundingMethod', intl),
    ]).then(([methods, rounding]) => {
      setUpdateMethods(methods);
      setRoundingMethods(rounding);
    });
  }, [intl]);

  const isFormInvalid = useCallback(() => {
    if (!amountOrPercentage || parseFloat(amountOrPercentage) === 0) return true;
    if (selectedUpdateMethod === 0 && (amountOrPercentage > 100 || amountOrPercentage < -100))
      return true;
    if (showRecalcRegistrations && updateRegistrations) {
      if (!registrationStartDate || !registrationEndDate) return true;
      if (!registrationStartDate.isValid()) return true;
      if (!registrationEndDate.isValid()) return true;
      if (registrationEndDate.isBefore(registrationStartDate)) return true;
    }

    return false;
  }, [
    amountOrPercentage,
    selectedUpdateMethod,
    showRecalcRegistrations,
    updateRegistrations,
    registrationStartDate,
    registrationEndDate,
  ]);

  const handleAmountChanged = value => {
    let parsedValue = Number.parseFloat(value);
    if (Number.isNaN(parsedValue)) {
      parsedValue = null;
    } else if (selectedUpdateMethod === 0) {
      parsedValue = Math.min(100, Math.max(-100, parsedValue));
    }
    setTouched(true);
    setAmountOrPercentage(parsedValue);
  };

  const handleSave = () => {
    setTouched(true);
    const payload = {
      method: selectedUpdateMethod,
      amountOrPercentage,
      rounding: selectedRoundingMethod,
      roundingDecimals,
    };
    const regUpdate = {};
    if (showRecalcRegistrations) {
      regUpdate.updateRegistrations = updateRegistrations;
      if (updateRegistrations && registrationStartDate && registrationEndDate) {
        regUpdate.startDate = registrationStartDate.toISOString();
        regUpdate.endDate = registrationEndDate.toISOString();
      }
    }
    return onSave(payload, regUpdate);
  };

  const generateExample = () =>
    utils.calculateIndexedAmount(
      Number.parseFloat(exampleAmount),
      selectedUpdateMethod,
      amountOrPercentage,
      selectedRoundingMethod,
      roundingDecimals,
    );

  if (!show) return null;

  const amountClassName = classNames('form-group', {
    'has-error': touched && (!amountOrPercentage || parseFloat(amountOrPercentage) === 0),
  });
  const generatedAmount = generateExample();

  return (
    <OkCancelModal
      title={title}
      show={show}
      onClose={onClose}
      disabled={isFormInvalid()}
      onSave={handleSave}
    >
      <div className="form-horizontal">
        <div className="form-group">
          <label className="control-label col-sm-4">{intl.formatMessage(messages.Method)}</label>
          <div className="col-sm-5">
            {updateMethods && (
              <select
                onChange={e => setSelectedUpdateMethod(parseInt(e.target.value))}
                className="form-control"
                value={selectedUpdateMethod}
              >
                {updateMethods.map(item => (
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        <div className={amountClassName}>
          <label className="control-label col-sm-4">
            {selectedUpdateMethod === 0
              ? intl.formatMessage(messages.Percentage)
              : intl.formatMessage(messages.Amount)}
          </label>
          <div className="col-sm-5">
            {(selectedUpdateMethod === 1 || selectedUpdateMethod === 2) && (
              <div className="input-group">
                <input
                  type="number"
                  step="1"
                  value={amountOrPercentage}
                  onChange={e => handleAmountChanged(e.target.value)}
                  className="form-control"
                />
                <span className="input-group-addon">
                  <CurrencySymbol />
                </span>
              </div>
            )}
            {selectedUpdateMethod === 0 && (
              <div className="input-group">
                <input
                  type="number"
                  step="1"
                  min="-100"
                  max="100"
                  value={amountOrPercentage}
                  onChange={e => handleAmountChanged(e.target.value)}
                  className="form-control"
                />
                <span className="input-group-addon">%</span>
              </div>
            )}
          </div>
        </div>

        {selectedUpdateMethod === 0 && (
          <div className="form-group">
            <label className="control-label col-sm-4">
              {intl.formatMessage(messages.RoundingMethod)}
            </label>
            <div className="col-sm-5">
              {roundingMethods && (
                <select
                  onChange={e => setSelectedRoundingMethod(parseInt(e.target.value))}
                  className="form-control"
                  value={selectedRoundingMethod}
                >
                  {roundingMethods.map(item => (
                    <option key={item.id} value={item.id}>
                      {item.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
        )}

        {selectedUpdateMethod === 0 && (
          <div className="form-group">
            <label className="control-label col-sm-4">{intl.formatMessage(messages.RoundTo)}</label>
            <div className="col-sm-5">
              <div className="input-group">
                <input
                  type="number"
                  onChange={e => setRoundingDecimals(parseInt(e.target.value))}
                  className="form-control"
                  min={0}
                  max={5}
                  value={roundingDecimals}
                />
                <span className="input-group-addon">{intl.formatMessage(messages.Digits)}</span>
              </div>
            </div>
          </div>
        )}

        <div className="form-group">
          <label className="control-label col-sm-4">{intl.formatMessage(messages.Example)}</label>
          <div className="col-sm-5">
            <div className="form-control-static inline-block">
              <FormattedCurrency value={exampleAmount} maximumFractionDigits={5} />
              <div className="text-center">&darr;</div>
              {!Number.isNaN(generatedAmount) && (
                <FormattedCurrency value={generatedAmount} maximumFractionDigits={5} />
              )}
            </div>
          </div>
        </div>

        {showRecalcRegistrations && (
          <>
            <div className="form-group">
              <div className="col-sm-8 col-sm-offset-4">
                <div className="checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={updateRegistrations}
                      onChange={e => setUpdateRegistrations(e.target.checked)}
                    />
                    {intl.formatMessage(messages.RecalculateRegistrationsWithNewPrice)}
                  </label>
                </div>
              </div>
            </div>
            {updateRegistrations && (
              <div className="form-group">
                <label className="control-label col-sm-4">
                  {intl.formatMessage(messages.RegistrationsBetween)}
                </label>
                <div className="col-sm-8">
                  <DelayedDateRangePicker
                    intl={intl}
                    startDate={registrationStartDate}
                    endDate={registrationEndDate}
                    startDateId="UpdateEmployeeAmountModalRegistrationsFrom"
                    endDateId="UpdateEmployeeAmountModalRegistrationsUntil"
                    onDatesChange={({ startDate, endDate }) => {
                      setRegistrationStartDate(startDate);
                      setRegistrationEndDate(endDate);
                    }}
                    block
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </OkCancelModal>
  );
};

UpdateEmployeeAmountModal.propTypes = {
  show: PropTypes.bool.isRequired,
  intl: intlShape.isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  exampleAmount: PropTypes.number,
  showRecalcRegistrations: PropTypes.bool,
};

UpdateEmployeeAmountModal.defaultProps = {
  exampleAmount: utils.randomAmount(),
  showRecalcRegistrations: false,
};

export default UpdateEmployeeAmountModal;
